import {
  createWhiteboardId,
  WhiteboardCanvasController,
  type WhiteboardCanvasEnvironment,
  type WhiteboardInteractionTool,
  type WhiteboardSessionSnapshot,
} from "@klass-share/whiteboard-core";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { MaximizeToggle } from "../session-ui.js";
import { strings } from "../strings.js";
import { whiteboardEditingEnabled } from "./editing.js";
import type { WhiteboardSession } from "./session.js";
import {
  WbGateChips,
  WbStatus,
  WbToolDock,
  type WhiteboardUiTool,
  whiteboardGateReasons,
  whiteboardPagePosition,
} from "./whiteboard-ui.js";

export interface WhiteboardTextPlacement {
  readonly x: number;
  readonly y: number;
  readonly left: number;
  readonly top: number;
}

export function whiteboardTextPlacementFromClientPoint(
  clientX: number,
  clientY: number,
  rect: Pick<DOMRect, "left" | "top" | "width" | "height">,
  logicalSize: { readonly width: number; readonly height: number },
): WhiteboardTextPlacement {
  const cssX = clamp(clientX - rect.left, 0, Math.max(0, rect.width));
  const cssY = clamp(clientY - rect.top, 0, Math.max(0, rect.height));
  const width = Math.max(1, rect.width);
  const height = Math.max(1, rect.height);
  return {
    x: (cssX * Math.max(1, logicalSize.width)) / width,
    y: (cssY * Math.max(1, logicalSize.height)) / height,
    left: clamp(cssX, 8, Math.max(8, rect.width - 288)),
    top: clamp(cssY, 8, Math.max(8, rect.height - 104)),
  };
}

interface ActiveWhiteboardTextPlacement extends WhiteboardTextPlacement {
  readonly pageId: string;
}

interface WhiteboardTextTapGesture {
  readonly pointerId: number;
  readonly startX: number;
  readonly startY: number;
  readonly pageId: string;
  readonly session: WhiteboardSession;
  moved: boolean;
}

const TEXT_TAP_SLOP_PX = 8;

const BROWSER_CANVAS_ENVIRONMENT: WhiteboardCanvasEnvironment = {
  createCanvas: () => document.createElement("canvas"),
  createPath: () => new Path2D(),
  ...(typeof createImageBitmap === "function"
    ? { createImageBitmap: (source) => createImageBitmap(source) }
    : {}),
  requestAnimationFrame: (callback) => requestAnimationFrame(callback),
  cancelAnimationFrame: (handle) => cancelAnimationFrame(handle),
  viewport: () => ({
    width: innerWidth,
    height: innerHeight,
    devicePixelRatio: Math.max(1, devicePixelRatio || 1),
  }),
  supportsPointerEvents: () => typeof PointerEvent === "function",
};

export interface WhiteboardViewProps {
  readonly session: WhiteboardSession | undefined;
  readonly visible: boolean;
  readonly deviceClass: string;
  /** Double-gated visual-audit projection; never supplied by the release app. */
  readonly visualAudit?: WhiteboardVisualAuditState;
}

export interface WhiteboardVisualAuditState {
  readonly activePage: string;
  readonly boardLocked: boolean;
  readonly canRedo: boolean;
  readonly canUndo: boolean;
  readonly drawingVerified: boolean;
  readonly hostTimeAnchored: boolean;
  readonly pageCount: number;
  readonly pageIndex: number;
  readonly persistenceSynced: boolean;
  readonly readyState: number;
}

export function WhiteboardView({
  session,
  visible,
  deviceClass,
  visualAudit,
}: WhiteboardViewProps): React.JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const controllerRef = useRef<WhiteboardCanvasController | undefined>(
    undefined,
  );
  const textInputRef = useRef<HTMLInputElement>(null);
  const textTapRef = useRef<WhiteboardTextTapGesture | undefined>(undefined);
  const [snapshot, setSnapshot] = useState(() =>
    visualAudit === undefined
      ? session?.snapshot
      : visualAuditSnapshot(visualAudit),
  );
  const [tool, setTool] = useState<WhiteboardInteractionTool>("pen");
  const [textMode, setTextMode] = useState(false);
  const [color, setColor] = useState("#17323d");
  const [width, setWidth] = useState(3);
  const [text, setText] = useState("");
  const [textPlacement, setTextPlacement] =
    useState<ActiveWhiteboardTextPlacement>();
  const [message, setMessage] = useState<string>();
  const [maximized, setMaximized] = useState(false);
  const [gateRevealNonce, setGateRevealNonce] = useState(0);

  useEffect(() => {
    if (!visible) setMaximized(false);
  }, [visible]);

  useEffect(() => {
    if (!maximized) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || textPlacement !== undefined) return;
      setMaximized(false);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [maximized, textPlacement]);

  useEffect(() => {
    if (visualAudit !== undefined) {
      setSnapshot(visualAuditSnapshot(visualAudit));
      return;
    }
    setSnapshot(session?.snapshot);
    if (session === undefined) return;
    return session.subscribe(setSnapshot);
  }, [session, visualAudit]);

  const page = useMemo(
    () =>
      visualAudit !== undefined
        ? { visualAudit: true }
        : session === undefined || snapshot === undefined
          ? undefined
          : session.model.pageSnapshot(snapshot.activePage),
    [session, snapshot, visualAudit],
  );
  const drawingVerified =
    visualAudit?.drawingVerified ?? deviceClass !== "webos";
  const editable = whiteboardEditingEnabled({
    sessionAvailable: session !== undefined || visualAudit !== undefined,
    pageAvailable: page !== undefined,
    drawingVerified,
    snapshot,
  });
  const pagePosition =
    visualAudit === undefined
      ? whiteboardPagePosition(
          session?.model.listPages() ?? [],
          snapshot?.activePage,
        )
      : {
          index: visualAudit.pageIndex,
          count: visualAudit.pageCount,
        };
  const boardLocked = snapshot?.boardLocked === true;
  const gateReasons = whiteboardGateReasons({
    boardLocked,
    drawingVerified,
    hostTimeAnchored: snapshot?.hostTimeAnchored === true,
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null || session === undefined) return;
    // The session is normally attached after this view's first render.  Do not
    // gate controller creation on the mirrored React snapshot: the subscription
    // effect updates that state in a later render while this effect is keyed to
    // the session lifetime.  Reading the session's synchronous snapshot here
    // keeps the canvas controller on the same lifetime without missing that
    // initialization boundary.
    const initialSnapshot = session.snapshot;
    const controller = new WhiteboardCanvasController({
      canvas,
      model: session.model,
      activePage: initialSnapshot.activePage,
      editable,
      createId: () => createWhiteboardId(crypto),
      environment: BROWSER_CANVAS_ENVIRONMENT,
      reducedMotion: motionReductionRequested(),
      now: () => session.createdAt(),
      onCursor: (cursor) => session.updateCursor(cursor),
      onPreview: (preview) => session.updatePreview(preview),
      onError: () => setMessage(strings.whiteboard.inputFailed),
    });
    controller.setTool(tool);
    controller.setColor(color);
    controller.setWidth(width);
    controller.setRemoteAwarenessWithIdentity(
      session.remoteAwarenessWithIdentity(),
    );
    controller.setVisible(visible);
    controllerRef.current = controller;
    const onResize = () => controller.resize();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      controller.destroy();
      controllerRef.current = undefined;
    };
  }, [session]);

  useEffect(() => {
    if (import.meta.env.VITE_KLASS_E2E !== "1" || visualAudit === undefined)
      return;
    const canvas = canvasRef.current;
    if (canvas === null) return;
    const drawReference = () => drawVisualAuditReference(canvas);
    drawReference();
    window.addEventListener("resize", drawReference);
    return () => window.removeEventListener("resize", drawReference);
  }, [visualAudit]);

  useEffect(() => {
    const controller = controllerRef.current;
    if (
      controller === undefined ||
      session === undefined ||
      snapshot === undefined
    )
      return;
    controller.setActivePage(snapshot.activePage);
    controller.setEditable(editable);
    controller.setRemoteAwarenessWithIdentity(
      session.remoteAwarenessWithIdentity(),
    );
    controller.setVisible(visible);
  }, [editable, session, snapshot, visible]);

  useEffect(() => controllerRef.current?.setTool(tool), [tool]);
  useEffect(() => controllerRef.current?.setColor(color), [color]);
  useEffect(() => controllerRef.current?.setWidth(width), [width]);

  const activeTextPlacement =
    textPlacement?.pageId === snapshot?.activePage ? textPlacement : undefined;

  useLayoutEffect(() => {
    if (activeTextPlacement !== undefined) textInputRef.current?.focus();
  }, [activeTextPlacement]);

  useEffect(() => {
    textTapRef.current = undefined;
    setTextPlacement(undefined);
    setText("");
  }, [editable, session, snapshot?.activePage]);

  const selectTool = (nextTool: WhiteboardUiTool) => {
    const currentTool: WhiteboardUiTool = textMode ? "text" : tool;
    if (nextTool !== currentTool) {
      setTextPlacement(undefined);
      setText("");
    }
    if (nextTool === "text") {
      setTextMode(true);
      return;
    }
    setTextMode(false);
    setTool(nextTool);
  };

  const beginTextPlacement = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!editable || canvas === null || snapshot === undefined || !textMode)
      return;
    setTextPlacement({
      pageId: snapshot.activePage,
      ...whiteboardTextPlacementFromClientPoint(
        clientX,
        clientY,
        canvas.getBoundingClientRect(),
        { width: innerWidth, height: innerHeight },
      ),
    });
    setText("");
    setMessage(undefined);
  };

  const beginTextPlacementAtCenter = () => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect === undefined) return;
    beginTextPlacement(rect.left + rect.width / 2, rect.top + rect.height / 2);
  };

  const updateTextCursor = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (session === undefined || snapshot === undefined || canvas === null)
      return;
    const point = whiteboardTextPlacementFromClientPoint(
      clientX,
      clientY,
      canvas.getBoundingClientRect(),
      { width: innerWidth, height: innerHeight },
    );
    session.updateCursor({
      page: snapshot.activePage,
      x: point.x,
      y: point.y,
    });
  };

  const commitText = (event: React.FormEvent) => {
    event.preventDefault();
    if (
      !editable ||
      session === undefined ||
      snapshot === undefined ||
      activeTextPlacement === undefined ||
      text.length === 0
    )
      return;
    try {
      session.model.commitObject({
        objectId: createWhiteboardId(crypto),
        pageId: activeTextPlacement.pageId,
        kind: "text",
        x: activeTextPlacement.x,
        y: activeTextPlacement.y,
        text,
        color,
        width: Math.max(18, width * 6),
        createdAt: session.createdAt(),
      });
      setText("");
      setTextPlacement(undefined);
      setMessage(undefined);
    } catch {
      setMessage(strings.whiteboard.textAddFailed);
    }
  };

  return (
    <section
      className={`whiteboard-plane klass-wb ${visible ? "" : "plane-hidden"}${
        maximized ? " whiteboard-plane-max" : ""
      }`}
      aria-label={strings.whiteboard.viewAria}
      data-wb-editable={editable}
      data-wb-maximized={maximized ? "true" : undefined}
    >
      <MaximizeToggle
        maximized={maximized}
        onToggle={() => setMaximized((value) => !value)}
        enterAria={strings.whiteboard.maximizeEnterAria}
      />
      <div className="wb-floating-chrome">
        {!boardLocked && (
          <div className="wb-tool-stack">
            <WbToolDock
              tool={textMode ? "text" : tool}
              color={color}
              width={width}
              editable={editable}
              canUndo={snapshot?.canUndo === true}
              canRedo={snapshot?.canRedo === true}
              onTool={selectTool}
              onColor={setColor}
              onWidth={setWidth}
              onUndo={() => session?.undo()}
              onRedo={() => session?.redo()}
            />
            {textMode && editable && (
              <button
                type="button"
                className="wb-text-fallback"
                data-wb-text-fallback
                onClick={beginTextPlacementAtCenter}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  beginTextPlacementAtCenter();
                }}
              >
                {strings.whiteboard.addText}
              </button>
            )}
          </div>
        )}
        <WbStatus
          page={pagePosition}
          connected={snapshot?.readyState === 1}
          persistenceSynced={snapshot?.persistenceSynced === true}
        />
      </div>
      <WbGateChips reasons={gateReasons} revealNonce={gateRevealNonce} />
      {message !== undefined && (
        <p className="error-text" role="alert">
          {message}
        </p>
      )}
      <div
        className="whiteboard-canvas-stage"
        onPointerDown={
          // Tapping a non-editable board re-reveals whichever gate notice
          // explains it — locked, view-only, or host-time syncing.
          !editable && gateReasons.length > 0
            ? () => setGateRevealNonce((nonce) => nonce + 1)
            : undefined
        }
      >
        <div
          className="whiteboard-canvas-surface"
          data-whiteboard-canvas-surface
        >
          <canvas
            ref={canvasRef}
            className="whiteboard-canvas"
            aria-label={strings.whiteboard.drawingAreaAria}
          />
          {textMode && editable && (
            <button
              type="button"
              className="wb-text-placement-layer"
              data-wb-text-placement-layer
              aria-label={strings.whiteboard.chooseTextPositionAria}
              onPointerDown={(event) => {
                if (
                  !event.isPrimary ||
                  event.button !== 0 ||
                  session === undefined ||
                  snapshot === undefined
                ) {
                  textTapRef.current = undefined;
                  return;
                }
                event.preventDefault();
                textTapRef.current = {
                  pointerId: event.pointerId,
                  startX: event.clientX,
                  startY: event.clientY,
                  pageId: snapshot.activePage,
                  session,
                  moved: false,
                };
                event.currentTarget.setPointerCapture(event.pointerId);
                updateTextCursor(event.clientX, event.clientY);
              }}
              onPointerMove={(event) => {
                if (event.isPrimary)
                  updateTextCursor(event.clientX, event.clientY);
                const gesture = textTapRef.current;
                if (
                  gesture === undefined ||
                  gesture.pointerId !== event.pointerId ||
                  gesture.moved
                )
                  return;
                const deltaX = event.clientX - gesture.startX;
                const deltaY = event.clientY - gesture.startY;
                if (
                  deltaX * deltaX + deltaY * deltaY >
                  TEXT_TAP_SLOP_PX * TEXT_TAP_SLOP_PX
                )
                  gesture.moved = true;
              }}
              onPointerUp={(event) => {
                const gesture = textTapRef.current;
                textTapRef.current = undefined;
                const deltaX =
                  event.clientX - (gesture?.startX ?? event.clientX);
                const deltaY =
                  event.clientY - (gesture?.startY ?? event.clientY);
                if (
                  !event.isPrimary ||
                  event.button !== 0 ||
                  gesture === undefined ||
                  gesture.pointerId !== event.pointerId ||
                  gesture.moved ||
                  deltaX * deltaX + deltaY * deltaY >
                    TEXT_TAP_SLOP_PX * TEXT_TAP_SLOP_PX ||
                  gesture.session !== session ||
                  gesture.pageId !== snapshot?.activePage
                )
                  return;
                beginTextPlacement(event.clientX, event.clientY);
              }}
              onPointerCancel={(event) => {
                if (textTapRef.current?.pointerId === event.pointerId)
                  textTapRef.current = undefined;
              }}
              onClick={(event) => {
                if (event.detail !== 0) return;
                beginTextPlacementAtCenter();
              }}
            >
              {activeTextPlacement === undefined && (
                <span className="wb-text-placement-hint">
                  {strings.whiteboard.chooseTextPosition}
                </span>
              )}
            </button>
          )}
          {textMode && editable && activeTextPlacement !== undefined && (
            <form
              className="wb-text-placer"
              data-wb-text-placer
              data-wb-text-x={activeTextPlacement.x}
              data-wb-text-y={activeTextPlacement.y}
              style={{
                left: activeTextPlacement.left,
                top: activeTextPlacement.top,
              }}
              onPointerDown={(event) => event.stopPropagation()}
              onSubmit={commitText}
            >
              <label
                className="visually-hidden"
                htmlFor="whiteboard-inline-text"
              >
                {strings.whiteboard.textLabel}
              </label>
              <input
                ref={textInputRef}
                id="whiteboard-inline-text"
                value={text}
                onChange={(event) => setText(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    setText("");
                    setTextPlacement(undefined);
                  }
                }}
                placeholder={strings.whiteboard.textPlaceholder}
              />
              <button
                type="submit"
                data-wb-text-action="commit"
                disabled={text.length === 0}
              >
                {strings.whiteboard.done}
              </button>
              <button
                type="button"
                className="secondary"
                data-wb-text-action="cancel"
                onClick={() => {
                  setText("");
                  setTextPlacement(undefined);
                }}
              >
                {strings.whiteboard.cancel}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function motionReductionRequested(): boolean {
  return (
    typeof matchMedia === "function" &&
    matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function visualAuditSnapshot(
  state: WhiteboardVisualAuditState,
): WhiteboardSessionSnapshot {
  return {
    activePage: state.activePage,
    boardLocked: state.boardLocked,
    canRedo: state.canRedo,
    canUndo: state.canUndo,
    hostTimeAnchored: state.hostTimeAnchored,
    persistenceSynced: state.persistenceSynced,
    readyState: state.readyState,
  };
}

function drawVisualAuditReference(canvas: HTMLCanvasElement): void {
  const logicalWidth = Math.max(1, window.innerWidth);
  const logicalHeight = Math.max(1, window.innerHeight);
  canvas.width = logicalWidth;
  canvas.height = logicalHeight;
  const context = canvas.getContext("2d");
  if (context === null) return;
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, logicalWidth, logicalHeight);
  context.strokeStyle = "#d9e2e7";
  context.lineWidth = 1;
  const grid = Math.max(40, Math.round(logicalWidth / 8));
  for (let x = grid; x < logicalWidth; x += grid) {
    context.beginPath();
    context.moveTo(x, 0);
    context.lineTo(x, logicalHeight);
    context.stroke();
  }
  for (let y = grid; y < logicalHeight; y += grid) {
    context.beginPath();
    context.moveTo(0, y);
    context.lineTo(logicalWidth, y);
    context.stroke();
  }

  const size = Math.max(
    48,
    Math.min(120, Math.round(Math.min(logicalWidth, logicalHeight) * 0.18)),
  );
  const centerY = Math.round(logicalHeight * 0.56);
  canvas.dataset.visualReferenceCircleCenterX = String(
    Math.round(logicalWidth * 0.72),
  );
  canvas.dataset.visualReferenceCircleCenterY = String(centerY);
  canvas.dataset.visualReferenceCircleDiameter = String(size);
  context.lineWidth = Math.max(3, Math.round(size / 20));
  context.strokeStyle = "#00865a";
  context.strokeRect(
    Math.round(logicalWidth * 0.18) - size / 2,
    centerY - size / 2,
    size,
    size,
  );
  context.beginPath();
  context.arc(
    Math.round(logicalWidth * 0.72),
    centerY,
    size / 2,
    0,
    Math.PI * 2,
  );
  context.stroke();

  context.fillStyle = "#17323d";
  context.font = `${Math.max(18, Math.round(logicalWidth / 24))}px sans-serif`;
  context.fillText(
    "정사각형  ↔  원",
    Math.round(logicalWidth * 0.12),
    centerY + size,
  );
  context.font = `${Math.max(16, Math.round(logicalWidth / 28))}px sans-serif`;
  const koreanNfd = "한글".normalize("NFD");
  context.fillText(
    `한글 NFC · ${koreanNfd} NFD`,
    Math.round(logicalWidth * 0.12),
    centerY + size + Math.max(28, Math.round(logicalHeight * 0.04)),
  );
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}
